import {ref} from 'vue';
import {defineStore} from 'pinia';
import {CrmService} from '@/services';
import type {EntityName} from '@/crm/spec/types';

interface AttributeMetadata {
    LogicalName: string;
    DisplayName?: {
        UserLocalizedLabel?: {
            Label?: string;
        };
    };
}

type EntityMetadata = Record<string, AttributeMetadata>;

export const useCrmMetadataStore = defineStore('crmMetadata', () => {
    const entities = ref<Partial<Record<EntityName, EntityMetadata>>>({});
    const inflight = new Map<EntityName, Promise<void>>();

    async function ensureEntity(name: EntityName): Promise<void> {
        if (entities.value[name]) return;
        const existing = inflight.get(name);
        if (existing) return existing;

        const p = (async () => {
            const crm = new CrmService();
            const list = await crm.getEntityFieldDefinitions(name);
            const map: EntityMetadata = {};
            for (const attr of (list as AttributeMetadata[]) || []) {
                if (attr?.LogicalName) map[attr.LogicalName] = attr;
            }
            entities.value = {...entities.value, [name]: map};
        })().finally(() => inflight.delete(name));

        inflight.set(name, p);
        return p;
    }

    function getLabel(entity: EntityName, field: string): string {
        const attr = entities.value[entity]?.[field];
        return attr?.DisplayName?.UserLocalizedLabel?.Label || field;
    }

    return {entities, ensureEntity, getLabel};
});
